import React, { useCallback, useEffect, useMemo, useState } from "react";

import ComponentLoading from "../../../modules/ComponentLoading";
import NoData from "../../../modules/NoData";
import { SearchUrl, SignatureUrl } from "../../navigation/RedFlagUrls";
import dayjs from "dayjs";
import SignatureTreeStore from "../../../stores/SignatureTreeStore";
import SignatureTreeActions from "../../../actions/SignatureTreeActions";
import { Link } from "react-router";
import DashboardRedFlagItem from "./DashboardRedFlagItem";
import { useUserStore } from "../../../storez/UserStore";
import { InfoIconV3 } from "../../../modules/InfoIconV3";
import { keywordRegionMatch, signatureRedFlagsByRegion } from "../UserDashboard/RedFlagsCardHelper";

const DashboardRedFlags = (props) => {
  const [signatureKeywords, setSignatureKeywords] = useState([]);
  const [formattedData, setFormattedData] = useState([]);

  // componentDidMount
  useEffect(() => {
    SignatureTreeStore.listen(updateKeywords);

    setTimeout(function () {
      SignatureTreeActions.fetch();
    }, 0);
  }, []);

  // componentWillUnmount
  useEffect(() => {
    return () => {
      SignatureTreeStore.unlisten(updateKeywords);
    };
  }, []);

  const updateKeywords = () => {
    const keywords = SignatureTreeStore.getKeywords();
    setSignatureKeywords(keywords);
  };

  const formatTime = (now, hitDatetime) => {
    const flagTime = dayjs(hitDatetime);
    let displayTime = flagTime.format("MMM DD YYYY, h:mm a");
    const diff = Math.abs(flagTime.diff(now, "minutes"));
    if (diff <= 1) {
      displayTime = "1 minute ago";
    } else if (diff < 60) {
      displayTime = `${diff} minutes ago`;
    } else if (diff < 1440) {
      const hours = Math.abs(flagTime.diff(now, "hours"));
      displayTime = `${hours} hours ago`;
    } else {
      const days = Math.abs(flagTime.diff(now, "days"));
      displayTime = `${days} days ago`;
    }
    return displayTime;
  };

  const getFlagList = useCallback((user, dateFilter, keywords, signatures, flagText) => {
    let flags = [];
    // Iterating through this because it combines search and non-search flags in one object
    const filteredKeywords = keywordRegionMatch(keywords);
    Object.keys(user.normals).forEach((flagName) => {
      if (user.normals[flagName] > 0) {
        let link;
        let hits;
        let type;
        if (signatures.find((signature) => signature.id === flagName)) {
          const signatureObj = signatures.find((signature) => signature.id === flagName);
          link = SignatureUrl(user.name, signatureObj.signature_id, dateFilter);
          hits = user[flagName];
          type = "non-search";
        } else if (!filteredKeywords.find((keyword) => keyword.id === flagName)) {
          return;
        } else {
          link = SearchUrl(user.name, flagText[flagName], dateFilter);
          hits = user["search_indicators"][flagName];
          type = "search";
        }
        const normalisedScore = user["normals"][flagName];
        const severityLevel = normalisedScore > 5 ? "high" : normalisedScore > 2 ? "medium" : "low";

        flags.push({
          name: flagText[flagName],
          level: severityLevel,
          link: link,
          hits: hits,
          type: type,
        });
      }
    });
    return flags;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUsersAsMap = useUserStore((state) => state.getUsersAsMap);
  const [redFlagsLoading, setRedFlagsLoading] = useState(false);

  async function formatFlags(data) {
    if (!data || redFlagsLoading || signatureKeywords.length === 0) {
      return;
    }
    const signatures = signatureRedFlagsByRegion();
    setRedFlagsLoading(true);

    const flagText = {};
    signatures.forEach((signature) => {
      flagText[signature.id] = signature.name;
    });

    signatureKeywords.forEach((keyword) => {
      flagText[keyword.id] = keyword.name;
    });

    const redFlagsCount = Math.min(6, data.length);
    const flagsToShow = data.slice(0, redFlagsCount);

    const usernames = flagsToShow.map((element) => element.name);
    const users = await getUsersAsMap(usernames);

    let results = [];
    const now = dayjs();
    const dateFilter =
      props.startDate && props.endDate
        ? `startDate=${props.startDate}&endDate=${props.endDate}&startTime=${props.startTime ? props.startTime : "00:00:00"}&endTime=${
            props.endTime ? props.endTime : "23:59:59"
          }`
        : "";

    flagsToShow.forEach((element) => {
      const user = users.get(element.name);
      const name = user && user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : element.name;
      const result = {
        name: name,
        nameLink: `/surfwize/reporting/users/${element.name}?${dateFilter}`,
        time: formatTime(now, element.most_recent_hit_datetime),
        flags: getFlagList(element, dateFilter, signatureKeywords, signatures, flagText),
      };

      results.push(result);
    });

    setFormattedData(results);
    setRedFlagsLoading(false);
  }

  useMemo(() => {
    if (props && props.data && props.data.sorted_users_highest_score) {
      formatFlags(props.data.sorted_users_highest_score).catch((error) => {
        setRedFlagsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, getFlagList, signatureKeywords]);

  const showLoading = () => {
    return props.isQueryLoading || signatureKeywords.length === 0 || redFlagsLoading;
  };

  const showNoData = () => {
    return !showLoading() && formattedData.length === 0;
  };

  const redFlagsContent = () => {
    if (showLoading()) {
      return (
        <div className="centered">
          <ComponentLoading />
        </div>
      );
    }
    if (showNoData()) {
      return <NoData />;
    }
    return (
      <div>
        {formattedData?.map(({ name, nameLink, time, flags }, index) => (
          <DashboardRedFlagItem time={time} name={name} nameLink={nameLink} flags={flags} key={index} />
        ))}
      </div>
    );
  };

  return (
    <div className="personaModule">
      <div className="personaModule-header">
        <h2>Red Flags</h2>
        <InfoIconV3 tooltipText="Risk indicators by user. Rollover for summary. Click for detail" />
        <Link to="/cybersafety/wellbeing" className="personaModule-rightLink" aria-label="Red Flags. View All">
          View All
        </Link>
      </div>
      {redFlagsContent()}
    </div>
  );
};

export default DashboardRedFlags;
